import api from './api';

/**
 * Analysis Storage Service
 * Handles saving and retrieving AI analyses (photos, voice, text notes)
 */
class AnalysisStorageService {
  /**
   * Save photo analysis
   * @param {Object} data - Photo analysis data
   * @returns {Promise<Object>} Saved analysis
   */
  async savePhotoAnalysis(data) {
    try {
      const response = await api.post('/ai-analyses/photo', data);
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Failed to save photo analysis:', error);
      throw error;
    }
  }

  /**
   * Save voice transcription
   * @param {Object} data - Voice transcription data
   * @returns {Promise<Object>} Saved transcription
   */
  async saveVoiceTranscription(data) {
    try {
      const response = await api.post('/ai-analyses/voice', data);
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Failed to save voice transcription:', error);
      throw error;
    }
  }

  /**
   * Save text note
   * @param {Object} data - Text note data
   * @returns {Promise<Object>} Saved text note
   */
  async saveTextNote(data) {
    try {
      const response = await api.post('/ai-analyses/text', data);
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Failed to save text note:', error);
      throw error;
    }
  }

  /**
   * Get all analyses for current user
   * @param {Object} filters - Filter options
   * @returns {Promise<Array>} List of analyses
   */
  async getAnalyses(filters = {}) {
    try {
      const response = await api.get('/ai-analyses', { params: filters });
      return response.data;
    } catch (error) {
      console.error('Failed to get analyses:', error);
      throw error;
    }
  }

  /**
   * Get analysis by ID
   * @param {string} id - Analysis ID
   * @returns {Promise<Object>} Analysis data
   */
  async getAnalysisById(id) {
    try {
      const response = await api.get(`/ai-analyses/${id}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get analysis:', error);
      throw error;
    }
  }

  /**
   * Delete analysis
   * @param {string} id - Analysis ID
   * @returns {Promise<void>}
   */
  async deleteAnalysis(id) {
    try {
      await api.delete(`/ai-analyses/${id}`);
    } catch (error) {
      console.error('Failed to delete analysis:', error);
      throw error;
    }
  }

  /**
   * Save generated report
   * @param {Object} data - Report data
   * @returns {Promise<Object>} Saved report
   */
  async saveReport(data) {
    try {
      const response = await api.post('/ai-analyses/reports', data);
      return response.data;
    } catch (error) {
      console.error('Failed to save report:', error);
      throw error;
    }
  }

  /**
   * Save generated report (alias for saveReport)
   * @param {Object} data - Report data
   * @returns {Promise<Object>} Saved report
   */
  async saveGeneratedReport(data) {
    return this.saveReport(data);
  }

  /**
   * Get report history
   * @param {Object} filters - Filter options
   * @returns {Promise<Array>} List of reports
   */
  async getReportHistory(filters = {}) {
    try {
      const response = await api.get('/ai-analyses/reports', { params: filters });
      return response.data;
    } catch (error) {
      console.error('Failed to get report history:', error);
      throw error;
    }
  }
}

export default new AnalysisStorageService();
